import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, NgZone, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { ColorPickerFieldComponent } from 'src/app/base/shared/color-picker-field/color-picker-field.component';
import { ThemeService } from 'src/app/base/services/theme.service';
import { tenantThemeOverrideVariables } from 'src/app/base/services/tenant-theme-overrides.constants';
import { ShowcaseOption } from '../../utils/theme-showcase-data';
import { ThemeTokenOverrideRow, buildOverridesFromRows, sortThemeTokenOverrideRows } from '../../utils/theme-token-override.util';
import {
  resolveThemeTokenOverrideAction,
  resolveThemeTokenOverrideValueForSelection
} from '../../utils/theme-token-override-rules.util';

type ThemeQuickEditorVariable = 'primary' | 'secondary' | string;

interface ThemeQuickEditorForm {
  variable: FormControl<ThemeQuickEditorVariable | null>;
  value: FormControl<string | null>;
}

const brandVariables: readonly ThemeQuickEditorVariable[] = ['primary', 'secondary'];

@Component({
  selector: 'app-theme-quick-editor',
  templateUrl: './theme-quick-editor.component.html',
  styleUrls: ['./theme-quick-editor.component.scss'],
  imports: [TranslatePipe, DropDownListModule, ReactiveFormsModule, ColorPickerFieldComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThemeQuickEditorComponent implements OnInit {
  public readonly dropdownFields = { text: 'label', value: 'value' };
  public variableOptions: ShowcaseOption[] = [];
  public isExpanded = false;
  public hasBeenOpened = false;

  private rowsValue: ThemeTokenOverrideRow[] = [];
  private primaryValue: string | null = '';
  private secondaryValue: string | null = '';

  @Input()
  public set rows(value: ThemeTokenOverrideRow[] | null) {
    this.rowsValue = value ?? [];
    this.syncValueWithSelection();
  }

  public get rows(): ThemeTokenOverrideRow[] {
    return this.rowsValue;
  }

  @Input()
  public set primary(value: string | null) {
    this.primaryValue = value ?? '';
    this.syncValueWithSelection();
  }

  @Input()
  public set secondary(value: string | null) {
    this.secondaryValue = value ?? '';
    this.syncValueWithSelection();
  }

  @Input() public originalPrimary = '';
  @Input() public originalSecondary = '';

  @Output() public rowsChange = new EventEmitter<ThemeTokenOverrideRow[]>();
  @Output() public primaryChange = new EventEmitter<string>();
  @Output() public secondaryChange = new EventEmitter<string>();

  public quickEditorForm = this.formBuilder.group<ThemeQuickEditorForm>({
    variable: this.formBuilder.control<ThemeQuickEditorVariable | null>(null),
    value: this.formBuilder.control<string | null>('')
  });

  public constructor(
    private readonly translate: TranslateService,
    private readonly themeService: ThemeService,
    private readonly formBuilder: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly ngZone: NgZone
  ) {
    this.buildVariableOptions();

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildVariableOptions();
      this.changeDetector.markForCheck();
    });

    // Token overrides in the quick editor always target the theme mode that is currently
    // active, so switching modes re-syncs the value shown for the selected token.
    this.themeService.mode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncValueWithSelection();
      this.changeDetector.markForCheck();
    });

    this.quickEditorForm.controls.variable.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncValueWithSelection());

    this.quickEditorForm.controls.value.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyDraftPreview());
  }

  public ngOnInit(): void {
    // Warm up the panel (Syncfusion dropdown/color picker) shortly after init, while it stays
    // hidden via CSS, so the first time the user opens it there is no visible initialization delay.
    this.ngZone.runOutsideAngular(() => {
      const scheduleWarmup = typeof requestIdleCallback === 'function' ? requestIdleCallback : (callback: () => void) => setTimeout(callback, 300);
      scheduleWarmup(() => {
        this.ngZone.run(() => {
          this.hasBeenOpened = true;
          this.changeDetector.markForCheck();
        });
      });
    });
  }

  public get isBrandVariableSelected(): boolean {
    return this.isBrandVariable(this.quickEditorForm.controls.variable.value);
  }

  public get currentModeTranslationKey(): string {
    return `i18n.domain.theme-showcase.theme.${this.themeService.getCurrentMode()}`;
  }

  public get saveButtonLabelKey(): string {
    if (this.isBrandVariableSelected) {
      return 'i18n.domain.theme-showcase.tenantTheme.quickEditor.saveButton';
    }

    const variable = this.selectedTokenVariable;
    const mode = this.themeService.getCurrentMode();
    return resolveThemeTokenOverrideAction(this.rows, variable, mode).action === 'update'
      ? 'i18n.domain.theme-showcase.tenantTheme.override.updateButton'
      : 'i18n.domain.theme-showcase.tenantTheme.override.addButton';
  }

  public get isTokenUpdateCase(): boolean {
    if (this.isBrandVariableSelected) {
      return false;
    }

    const variable = this.selectedTokenVariable;
    if (!variable) {
      return false;
    }

    const mode = this.themeService.getCurrentMode();
    return resolveThemeTokenOverrideAction(this.rows, variable, mode).action === 'update';
  }

  public toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      this.hasBeenOpened = true;
    }
  }

  public onValueChanged(value: string): void {
    this.quickEditorForm.controls.value.setValue(String(value ?? ''), { emitEvent: true });
  }

  public resetBrandColor(): void {
    const variable = this.quickEditorForm.controls.variable.value;
    if (!this.isBrandVariable(variable)) {
      return;
    }

    const originalValue = String((variable === 'primary' ? this.originalPrimary : this.originalSecondary) ?? '');

    // Set silently and emit the reset explicitly: applyDraftPreview() skips empty values (it
    // only guards against previewing an incomplete draft), which would otherwise prevent
    // resetting to "no override" when the original value is empty.
    this.quickEditorForm.controls.value.setValue(originalValue, { emitEvent: false });
    this.emitBrandColorChange(variable as 'primary' | 'secondary', originalValue);
  }

  public save(): void {
    const variable = String(this.quickEditorForm.controls.variable.value ?? '').trim();
    const value = String(this.quickEditorForm.controls.value.value ?? '').trim();

    if (!variable || !value) {
      return;
    }

    if (this.isBrandVariable(variable)) {
      this.emitBrandColorChange(variable as 'primary' | 'secondary', value);
      return;
    }

    const mode = this.themeService.getCurrentMode();
    const resolution = resolveThemeTokenOverrideAction(this.rows, variable, mode);

    let nextRows: ThemeTokenOverrideRow[];
    if (resolution.action === 'update' && resolution.targetRow) {
      const targetRow = resolution.targetRow;
      nextRows = this.rows.map((row) => (row !== targetRow ? row : { ...row, mode, value }));
    } else {
      nextRows = [...this.rows, { variable, mode, value }];
    }

    this.rowsValue = sortThemeTokenOverrideRows(nextRows);
    this.rowsChange.emit(this.rowsValue);
  }

  public deleteTokenOverride(): void {
    const variable = this.selectedTokenVariable;
    if (!variable || this.isBrandVariableSelected) {
      return;
    }

    const mode = this.themeService.getCurrentMode();
    this.rowsValue = this.rows.filter((row) => !(row.variable === variable && row.mode === mode));
    this.rowsChange.emit(this.rowsValue);

    this.quickEditorForm.controls.value.setValue('', { emitEvent: false });
    this.themeService.applyTenantThemeOverridesPreview(buildOverridesFromRows(this.rowsValue));
  }

  private get selectedTokenVariable(): string {
    return String(this.quickEditorForm.controls.variable.value ?? '').trim();
  }

  private isBrandVariable(variable: ThemeQuickEditorVariable | null): boolean {
    return brandVariables.includes(variable ?? '');
  }

  private emitBrandColorChange(control: 'primary' | 'secondary', value: string): void {
    if (control === 'primary') {
      this.primaryValue = value;
      this.primaryChange.emit(value);
    } else {
      this.secondaryValue = value;
      this.secondaryChange.emit(value);
    }
  }

  private buildVariableOptions(): void {
    this.variableOptions = [
      { label: this.translate.instant('i18n.domain.theme-showcase.tenantTheme.primary'), value: 'primary' },
      { label: this.translate.instant('i18n.domain.theme-showcase.tenantTheme.secondary'), value: 'secondary' },
      ...tenantThemeOverrideVariables.map((variable) => ({ label: variable, value: variable }))
    ];
  }

  private syncValueWithSelection(): void {
    if (this.isBrandVariableSelected) {
      const variable = this.quickEditorForm.controls.variable.value;
      const brandValue = variable === 'primary' ? this.primaryValue : this.secondaryValue;
      this.quickEditorForm.controls.value.setValue(brandValue ?? '', { emitEvent: false });

      // Discard any token draft preview left over from a previous selection.
      this.themeService.applyTenantThemeOverridesPreview(buildOverridesFromRows(this.rows));
      return;
    }

    const variable = this.selectedTokenVariable;
    const mode = this.themeService.getCurrentMode();
    const value = resolveThemeTokenOverrideValueForSelection(this.rows, variable, mode);
    this.quickEditorForm.controls.value.setValue(value, { emitEvent: false });

    // Selecting a variable/mode never carries over a draft preview from a previously selected
    // token: rebuild the preview from the committed rows plus (if any) this token's own
    // committed value, discarding whatever was being test-driven for the prior selection.
    this.applyDraftPreview();
  }

  /**
   * Live-previews the value currently being edited (mirroring how the primary/secondary color
   * pickers in the full editor already preview instantly, without needing to click Save first).
   */
  private applyDraftPreview(): void {
    const value = String(this.quickEditorForm.controls.value.value ?? '').trim();

    if (this.isBrandVariableSelected) {
      if (!value) {
        return;
      }

      this.emitBrandColorChange(this.quickEditorForm.controls.variable.value as 'primary' | 'secondary', value);
      return;
    }

    const variable = this.selectedTokenVariable;

    if (!variable || !value) {
      this.themeService.applyTenantThemeOverridesPreview(buildOverridesFromRows(this.rows));
      return;
    }

    const mode = this.themeService.getCurrentMode();
    const resolution = resolveThemeTokenOverrideAction(this.rows, variable, mode);

    let previewRows: ThemeTokenOverrideRow[];
    if (resolution.action === 'update' && resolution.targetRow) {
      const targetRow = resolution.targetRow;
      previewRows = this.rows.map((row) => (row !== targetRow ? row : { ...row, mode, value }));
    } else {
      previewRows = [...this.rows, { variable, mode, value }];
    }

    this.themeService.applyTenantThemeOverridesPreview(buildOverridesFromRows(previewRows));
  }
}
